import type { HeadId, StageId } from './content'

/**
 * The chat window around the reply: typing it out, frozen mid-sentence,
 * zooming into the 3D scene, and settling back in for the epilogue.
 */
export type ChatPhase = 'type' | 'hold' | 'enter' | 'return'

export type Beat = {
  caption: string
  ms: number
  /** Overrides the chapter's stage for this beat. */
  stage?: StageId
  chat?: ChatPhase
  /** Shows the blank, the next token's empty slot, from this beat on. */
  blank?: boolean
  head?: HeadId
  temperature?: number
  /** Adds the most likely next token shortly after the beat starts. */
  sample?: boolean
  /** Frames the prediction bars and the token row together. */
  shot?: 'wide'
}

export type Chapter = { title: string; short: string; stage: StageId; beats: Beat[] }

export const POSTER = {
  title: 'How an LLM writes',
  setup: 'Freeze a chatbot mid-reply, then step inside to watch it produce the next word.',
}

export const END_CARD = {
  title: 'Now you know what happens between every word.',
  line: 'Every reply from a language model is this loop, one token at a time.',
}

export const CHAPTERS: Chapter[] = [
  {
    title: 'A reply, frozen mid-sentence',
    short: 'Prologue',
    stage: 'text',
    beats: [
      { caption: 'You ask a question, and a reply starts to appear.', ms: 4800, chat: 'type' },
      { caption: "Let's freeze it right here, mid-sentence.", ms: 4000, chat: 'hold' },
      {
        caption: 'What comes next? The model has to produce one more word.',
        ms: 4800,
        chat: 'hold',
      },
      {
        caption: "It isn't looking anything up. Let's step inside and watch.",
        ms: 4800,
        chat: 'enter',
      },
    ],
  },
  {
    title: "It can't read",
    short: 'Tokens',
    stage: 'tokens',
    beats: [
      { caption: "First surprise: the model can't read letters.", ms: 4200, stage: 'text' },
      {
        caption: 'The text is cut into tokens: common words, or pieces of rarer ones.',
        ms: 4800,
      },
      {
        caption: "Each token becomes an ID number. That's all the model ever gets.",
        ms: 4800,
      },
      {
        caption: 'And the blank at the end is the token it has to predict.',
        ms: 4500,
        blank: true,
      },
    ],
  },
  {
    title: 'Numbers that mean something',
    short: 'Vectors',
    stage: 'embeddings',
    beats: [
      {
        caption: 'An ID is just a label, so each one looks up a list of numbers.',
        ms: 4800,
      },
      { caption: 'Real models use thousands of numbers per token. We show eight.', ms: 4500 },
      { caption: 'Nobody chose these values. Training tuned them.', ms: 4200 },
    ],
  },
  {
    title: 'A map of meaning',
    short: 'Meaning',
    stage: 'meaning',
    beats: [
      {
        caption: 'Read those numbers as coordinates, and every token gets a place.',
        ms: 5000,
      },
      { caption: 'Similar meanings end up as neighbors: cat sits near dog.', ms: 4500 },
      {
        caption: 'The real map has thousands of dimensions. We squashed it to three.',
        ms: 4500,
      },
    ],
  },
  {
    title: 'Looking for context',
    short: 'Attention',
    stage: 'attention',
    beats: [
      {
        caption: "One token alone can't fill the blank. It needs context.",
        ms: 4500,
        head: 'meaning',
      },
      { caption: 'So the last token looks back and scores every earlier token.', ms: 5000 },
      {
        caption: 'Thicker arcs mean more attention: these are its strongest clues.',
        ms: 4800,
      },
      {
        caption: 'Many attention heads look at once. This one checks the previous word.',
        ms: 5200,
        head: 'previous',
      },
      {
        caption: 'This one rests on the first token when nothing else stands out.',
        ms: 4800,
        head: 'first',
      },
      {
        caption: 'And no peeking ahead: tokens can only look left.',
        ms: 4500,
        head: 'meaning',
      },
    ],
  },
  {
    title: 'Again, and again',
    short: 'Layers',
    stage: 'layers',
    beats: [
      {
        caption: "One look isn't enough. Attention plus a small network makes a layer.",
        ms: 5000,
      },
      { caption: 'Big models stack dozens of layers. GPT-3 has 96.', ms: 4500 },
      {
        caption: "Layer by layer, the last token's vector sharpens into a guess.",
        ms: 4800,
      },
    ],
  },
  {
    title: 'The guess',
    short: 'The guess',
    stage: 'prediction',
    beats: [
      {
        caption: 'Finally, every token in the vocabulary gets a score.',
        ms: 4800,
        temperature: 1,
      },
      {
        caption: 'Softmax turns the scores into probabilities that add up to 100%.',
        ms: 4800,
      },
      {
        caption: 'Turn the temperature down, and the favorite dominates.',
        ms: 4200,
        temperature: 0.3,
      },
      {
        caption: 'Turn it up, and long shots get a real chance.',
        ms: 4200,
        temperature: 1.8,
      },
    ],
  },
  {
    title: 'The loop',
    short: 'The loop',
    stage: 'prediction',
    beats: [
      {
        caption: 'Pick one, and the blank is filled.',
        ms: 4200,
        temperature: 1,
        sample: true,
        shot: 'wide',
      },
      {
        caption: 'Then the whole trip runs again, for the next token.',
        ms: 4500,
        sample: true,
        shot: 'wide',
      },
      {
        caption: "That's why replies appear a piece at a time. Every piece made this trip.",
        ms: 5200,
        chat: 'return',
      },
      {
        caption: 'No lookup, no plan for the whole answer. One token, then the next.',
        ms: 4800,
        chat: 'return',
      },
    ],
  },
]

export function chapterLabel(chapter: number) {
  return chapter === 0 ? 'Prologue' : String(chapter).padStart(2, '0')
}
